import { ArrowLeft, Check, ExternalLink, Mail, Phone, Plus, Save, UserRound } from 'lucide-react'
import { useEffect, useState, type FormEvent } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { Modal } from '../components/Modal'
import { useEventContext } from '../contexts/EventContext'
import { getCompany, mutate } from '../services/api'
import type { Contact, Opportunity, Priority, Prospect } from '../types/crm'

const followUpStatuses = ['À préparer', 'À contacter', 'Contacté', 'RDV demandé', 'RDV confirmé', 'Rencontré', 'À relancer', 'Opportunité', 'Devis', 'Projet', 'Gagné', 'Perdu', 'Sans suite']
const progressValues = [0, 25, 50, 75, 100]

const nullableNumber = (value: FormDataEntryValue | null) => value === null || value === '' ? null : Number(value)
const text = (value: FormDataEntryValue | null) => String(value ?? '').trim()

export function CompanyPage() {
  const { companyId = '' } = useParams()
  const [params] = useSearchParams()
  const { selectedEvent } = useEventContext()
  const eventId = params.get('eventId') ?? selectedEvent?.id ?? ''
  const [company, setCompany] = useState<Prospect | null>(null)
  const [loading, setLoading] = useState(true)
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [contactOpen, setContactOpen] = useState(false)

  useEffect(() => {
    setLoading(true)
    void getCompany(companyId, eventId).then(setCompany).catch(() => setError('Impossible de charger cette enseigne.')).finally(() => setLoading(false))
  }, [companyId, eventId])

  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => { if (dirty) event.preventDefault() }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirty])

  const save = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!company) return
    setSaving(true); setError(null); setMessage(null)
    const data = new FormData(event.currentTarget)
    const patch = {
      eventId,
      name: text(data.get('name')),
      storeCount: nullableNumber(data.get('storeCount')),
      sector: text(data.get('sector')),
      country: text(data.get('country')),
      representativeCount: nullableNumber(data.get('representativeCount')),
      seniorProfileCount: nullableNumber(data.get('seniorProfileCount')),
      profilesPresent: text(data.get('profilesPresent')),
      priority: text(data.get('priority')) as Priority,
      eslOpportunity: text(data.get('eslOpportunity')) as Opportunity,
      enchantOpportunity: text(data.get('enchantOpportunity')) as Opportunity,
      elabelOpportunity: text(data.get('elabelOpportunity')) as Opportunity,
      currentEsl: text(data.get('currentEsl')),
      contactIdentifiedSummary: text(data.get('contactIdentifiedSummary')),
      contactToIdentify: text(data.get('contactToIdentify')),
      commercialAngle: text(data.get('commercialAngle')),
      eventAction: text(data.get('eventAction')),
      preparationProgress: Number(data.get('preparationProgress')),
      appointmentPlanned: data.get('appointmentPlanned') === 'on',
      met: data.get('met') === 'on',
      followUpStatus: text(data.get('followUpStatus')),
      nextAction: text(data.get('nextAction')),
      nextActionDate: text(data.get('nextActionDate')) || null,
      eventNotes: text(data.get('eventNotes')),
    }
    try {
      const result = await mutate(`/api/companies/${company.id}`, 'PATCH', patch)
      setCompany({ ...company, ...patch })
      setDirty(false)
      setMessage(result === null ? 'Modification conservée sur cet appareil, en attente de synchronisation.' : 'Modifications enregistrées.')
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Impossible d'enregistrer les modifications.")
    } finally { setSaving(false) }
  }

  if (loading) return <div className="loading-list">Chargement des informations…</div>
  if (!company || !selectedEvent) return <div className="empty-state"><h1>Enseigne introuvable</h1><Link to="/enseignes">Revenir à la liste</Link></div>

  return <div className="page edit-company-page">
    <Link className="back-link" to="/enseignes"><ArrowLeft size={17} />Retour aux enseignes</Link>
    <div className="edit-page-heading">
      <div><p className="eyebrow">{selectedEvent.name}</p><h1>Modifier {company.name}</h1><p>Tous les champs ci-dessous correspondent aux informations du tableau enseignes.</p></div>
      <span className={`priority large-priority priority-${company.priority}`}>Priorité {company.priority}</span>
    </div>

    <form className="source-edit-form" onSubmit={(event) => void save(event)} onChange={() => { setDirty(true); setMessage(null) }}>
      <section className="form-section">
        <div className="form-section-title"><span>1</span><div><h2>Enseigne</h2><p>Informations générales de la ligne Excel</p></div></div>
        <div className="field-grid">
          <label className="span-2">Société / enseigne<input name="name" defaultValue={company.name} required /></label>
          <label>Nombre de magasins approx.<input name="storeCount" type="number" min="0" defaultValue={company.storeCount ?? ''} /></label>
          <label>Secteur d’activité<input name="sector" defaultValue={company.sector ?? ''} /></label>
          <label>Pays<input name="country" defaultValue={company.country ?? ''} /></label>
        </div>
      </section>

      <section className="form-section">
        <div className="form-section-title"><span>2</span><div><h2>Présence au salon</h2><p>Représentants et profils attendus</p></div></div>
        <div className="field-grid">
          <label>Nombre de représentants<input name="representativeCount" type="number" min="0" defaultValue={company.representativeCount ?? ''} /></label>
          <label>Profils senior / direction<input name="seniorProfileCount" type="number" min="0" defaultValue={company.seniorProfileCount ?? ''} /></label>
          <label className="span-2">Profils présents<textarea name="profilesPresent" rows={3} defaultValue={company.profilesPresent ?? ''} /></label>
        </div>
      </section>

      <section className="form-section">
        <div className="form-section-title"><span>3</span><div><h2>Opportunités</h2><p>ESL, Enchant, e.Label et parc actuel</p></div></div>
        <div className="field-grid opportunity-fields">
          <OpportunitySelect name="eslOpportunity" label="Possibilité ESL" value={company.eslOpportunity} />
          <OpportunitySelect name="enchantOpportunity" label="Possibilité Enchant" value={company.enchantOpportunity} />
          <OpportunitySelect name="elabelOpportunity" label="Possibilité e.Label" value={company.elabelOpportunity} />
          <label>ESL actuellement présentes<input name="currentEsl" defaultValue={company.currentEsl ?? ''} placeholder="Vusion, Pricer, Hanshow…" /></label>
          <label className="span-2">Intérêt / angle ESL<textarea name="commercialAngle" rows={3} defaultValue={company.commercialAngle ?? ''} /></label>
        </div>
      </section>

      <section className="form-section contact-source-section">
        <div className="form-section-title"><span>4</span><div><h2>Contacts</h2><p>Texte original du tableau et contacts à rechercher</p></div></div>
        <div className="field-grid">
          <label className="span-2 important-field">Contact déjà identifié<textarea name="contactIdentifiedSummary" rows={5} defaultValue={company.contactIdentifiedSummary ?? ''} placeholder="Nom, fonction, coordonnées ou texte original de la cellule…" /><small>Le contenu original reste intact : vous pouvez le corriger ou le compléter librement.</small></label>
          <label className="span-2">Contact à identifier<textarea name="contactToIdentify" rows={3} defaultValue={company.contactToIdentify ?? ''} placeholder="Ex. Responsable Store Technology / Achats" /></label>
        </div>
        <div className="structured-contacts">
          <div className="structured-contacts-head"><h3>Fiches contacts</h3><button type="button" className="button secondary" onClick={() => setContactOpen(true)}><Plus size={17} />Ajouter un contact</button></div>
          {company.contacts?.length ? <div className="contact-list">{company.contacts.map((contact) => <ContactLine contact={contact} key={contact.id} />)}</div> : <p className="empty-contact-text">Aucune fiche contact créée. Le texte importé ci-dessus est conservé.</p>}
        </div>
      </section>

      <section className="form-section">
        <div className="form-section-title"><span>5</span><div><h2>Préparation et suivi</h2><p>Dernières colonnes du tableau salon</p></div></div>
        <div className="field-grid">
          <label>Priorité<select name="priority" defaultValue={company.priority}><option value="A">A</option><option value="B">B</option><option value="C">C</option></select></label>
          <label>Préparation<select name="preparationProgress" defaultValue={company.preparationProgress ?? 0}>{progressValues.map((value) => <option value={value} key={value}>{value} %</option>)}</select></label>
          <label className="span-2">Action avant / pendant salon<textarea name="eventAction" rows={4} defaultValue={company.eventAction ?? ''} /></label>
          <label>Statut de suivi<select name="followUpStatus" defaultValue={company.followUpStatus}>{followUpStatuses.map((status) => <option key={status}>{status}</option>)}</select></label>
          <label>Date prochaine action<input name="nextActionDate" type="date" defaultValue={company.nextActionDate ?? ''} /></label>
          <label className="span-2">Prochaine action<textarea name="nextAction" rows={3} defaultValue={company.nextAction ?? ''} /></label>
          <label className="span-2">Notes salon<textarea name="eventNotes" rows={5} defaultValue={company.eventNotes ?? ''} /></label>
          <div className="check-fields span-2"><label><input name="appointmentPlanned" type="checkbox" defaultChecked={Boolean(company.appointmentPlanned)} />Rendez-vous prévu</label><label><input name="met" type="checkbox" defaultChecked={Boolean(company.met)} />Enseigne rencontrée</label></div>
        </div>
      </section>

      <div className="save-bar">
        <div>{error && <span className="save-error">{error}</span>}{message && <span className="save-success"><Check size={16} />{message}</span>}{dirty && !error && !message && <span className="unsaved">Modifications non enregistrées</span>}</div>
        <button className="button primary save-button" disabled={saving || !dirty}><Save size={18} />{saving ? 'Enregistrement…' : 'Enregistrer les modifications'}</button>
      </div>
    </form>
    <AddContactModal open={contactOpen} company={company} eventId={eventId} onClose={() => setContactOpen(false)} onSaved={(contact) => { setCompany((current) => current ? { ...current, contacts: [...(current.contacts ?? []), contact] } : current); setContactOpen(false) }} />
  </div>
}

function OpportunitySelect({ name, label, value }: { name: string; label: string; value: Opportunity }) {
  return <label>{label}<select name={name} defaultValue={value}><option value="OUI">OUI</option><option value="NON">NON</option><option value="UNKNOWN">INCONNU</option></select></label>
}

function ContactLine({ contact }: { contact: Contact }) {
  return <article className="contact-line"><span className="contact-avatar"><UserRound size={18} /></span><div><strong>{contact.firstName} {contact.lastName}</strong><small>{contact.jobTitle || 'Fonction à compléter'}</small></div><div className="contact-line-actions">{contact.phone && <a href={`tel:${contact.phone}`} title="Appeler"><Phone size={17} /></a>}{contact.email && <a href={`mailto:${contact.email}`} title="Envoyer un email"><Mail size={17} /></a>}{contact.linkedinUrl && <a href={contact.linkedinUrl} target="_blank" rel="noreferrer" title="Ouvrir LinkedIn"><ExternalLink size={17} /></a>}</div></article>
}

function AddContactModal({ open, company, eventId, onClose, onSaved }: { open: boolean; company: Prospect; eventId: string; onClose: () => void; onSaved: (contact: Contact) => void }) {
  const [saving, setSaving] = useState(false)
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault(); setSaving(true); const data = new FormData(event.currentTarget)
    const contact = { id: crypto.randomUUID(), firstName: text(data.get('firstName')), lastName: text(data.get('lastName')), jobTitle: text(data.get('jobTitle')), email: text(data.get('email')), phone: text(data.get('phone')), linkedinUrl: text(data.get('linkedinUrl')) }
    await mutate('/api/contacts', 'POST', { ...contact, companyId: company.id, eventId, isPrimary: !(company.contacts?.length), present: false })
    setSaving(false); onSaved(contact)
  }
  return <Modal open={open} title={`Ajouter un contact — ${company.name}`} onClose={onClose}><form className="stack-form" onSubmit={(event) => void submit(event)}>
    <div className="form-columns"><label>Prénom<input name="firstName" autoFocus /></label><label>Nom<input name="lastName" required /></label></div>
    <label>Fonction<input name="jobTitle" /></label><label>Email<input name="email" type="email" /></label><label>Téléphone<input name="phone" type="tel" /></label><label>LinkedIn<input name="linkedinUrl" type="url" /></label>
    <div className="modal-actions"><button type="button" className="button secondary" onClick={onClose}>Annuler</button><button className="button primary" disabled={saving}>{saving ? 'Ajout…' : 'Ajouter le contact'}</button></div>
  </form></Modal>
}
